import "./index.less";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "antd";
import ShopType from "@/enum/ShopType";
import AddOrderButton from "@/scenes/OrderManagement/components/BottomOperationBar/AddOrderButton";
import PrintWaybillButton from "./PrintWaybillButton";
import RestoreOrderButton from "./RestoreOrderButton";
import OtherOperationsButton from "./OtherOperationsButton/index";
import FavoriteOperationsButtons from "./OtherOperationsButton/components/FavoriteOperationsButtons";
import ShipButton from "./ShipButton";
import CancelOrderButton from "./CancelOrderButton";
import OrderTab from "@/scenes/OrderManagement/enum/OrderTab";
import ShopDTO from "@/dto/shop/ShopDTO";
import DeleteAbnormalOrderButton from "./DeleteAbnormalOrderButton";
import SetHasLogisticsInfoButton from "./SetHasLogisticsInfoButton";
import BackTopButton from "./BackTopButton";
import Pagination from "../OrderTable/Pagination";
import SelectAllCheckbox from "../OrderTable/OrderTableHead/components/SelectAllCheckbox";
import { observer } from "mobx-react-lite";
import TabPreShipActionButtons from "./TabPreShipActionButtons";
import { useOrderManagementStore } from "../../store/OrderManagementStore";
import { useSize } from "ahooks";
import { throttle } from "lodash";
import { reaction } from "mobx";
import SelectedOrderInfo from "./SelectedOrderInfo";
import { useBottomBarAutoWrap } from "@/hooks/useBottomBarAutoWrap";
import { isJdDropshipShop } from "@/utils/ShopUtils";
import { useModalStore } from "@/hooks/Modal/hooks";
import EwaybillAuthModal from "../OrderTable/Pagination/EwaybillAuthModal";
import OutBoundButton from "./OutBoundButton";
import RunningStatus from "../InstantShip/RunningStatus";
import "../InstantShip/index.less";

// 能取消订单的店铺类型
const canCancelShopTypes = [ShopType.MANUAL, ShopType.DALINGJIA, ShopType.INDEPENDENT_MANUAL];

const canCancelOrderShop = (shop: ShopDTO) => canCancelShopTypes.includes(shop.type);

const OperationButtons: React.FC<BottomOperationBarProps> = observer(function (props) {
    const { isSupplierShop, shop, tab } = props;

    const orderManagementStore = useOrderManagementStore();

    const isShowCancelButton = useMemo(() => {
        return canCancelOrderShop(shop) && tab !== OrderTab.CANCELED;
    }, [shop?.type, tab]);

    const getSelectedOrders = useCallback(() => {
        if (!orderManagementStore.table.selectedOrders.length) {
            Modal.error({
                title: "请先至少选择一个订单",
            });

            throw new Error("请先至少选择一个订单");
        }

        return orderManagementStore.table.selectedOrders;
    }, []);

    if (isSupplierShop) {
        if (tab === OrderTab.PENDING_PAYMENT) {
            return null;
        }
    }

    if (tab === OrderTab.PRE_SHIP) {
        return (
            <TabPreShipActionButtons
                tab={tab}
                shop={shop}
                getSelectedOrders={getSelectedOrders}
                isSupplierShop={isSupplierShop}
            />
        );
    }

    return (
        <>
            {shop.type === ShopType.MANUAL || shop.type === ShopType.INDEPENDENT_MANUAL ? (
                <AddOrderButton shop={shop} />
            ) : null}

            {tab === OrderTab.ABNORMAL && <DeleteAbnormalOrderButton getSelectedOrders={getSelectedOrders} tab={tab} />}

            {[OrderTab.LOGISTICS_WARNING, OrderTab.PICKUP_DEADLINE_WARNING].includes(tab) && (
                <SetHasLogisticsInfoButton getSelectedOrders={getSelectedOrders} tab={tab} />
            )}

            <PrintWaybillButton tab={tab} shop={shop} />

            {isJdDropshipShop(shop) && tab === OrderTab.PENDING_PAYMENT ? (
                <OutBoundButton tab={tab} shop={shop} getSelectedOrders={getSelectedOrders} />
            ) : null}

            <ShipButton shop={shop} tab={tab} getSelectedOrders={getSelectedOrders} />

            {tab === OrderTab.ON_HOLD ? <RestoreOrderButton getSelectedOrders={getSelectedOrders} shop={shop} /> : null}

            <FavoriteOperationsButtons
                tab={tab}
                shop={shop}
                getSelectedOrders={getSelectedOrders}
                isSupplierShop={isSupplierShop}
            />

            <OtherOperationsButton
                tab={tab}
                getSelectedOrders={getSelectedOrders}
                shop={shop}
                isSupplierShop={isSupplierShop}
            />

            {isShowCancelButton ? (
                <CancelOrderButton getSelectedOrders={getSelectedOrders} shop={shop} tab={tab} />
            ) : null}
        </>
    );
});

interface BottomOperationBarProps {
    shop?: ShopDTO;
    tab?: OrderTab;
    isSupplierShop?: boolean;
}

const BottomOperationBar: React.FC<BottomOperationBarProps> = function (props) {
    const { shop, tab } = props;

    const orderManagementStore = useOrderManagementStore();

    const [affixStyle, setAffixStyle] = useState<React.CSSProperties | undefined>(undefined);

    const wrapperRef = useRef<HTMLDivElement | null>(null);

    const barRef = useRef<HTMLDivElement | null>(null);

    const barSize = useSize(barRef);

    const { metaRef, actionsRef, isWrapped, shiftRight } = useBottomBarAutoWrap(barSize);

    const updatePosition = useMemo(
        () =>
            throttle(() => {
                const mainRect = document.querySelector(".order-main")?.getBoundingClientRect();
                const wrapperRect = wrapperRef.current?.getBoundingClientRect();
                const barRect = barRef.current?.getBoundingClientRect();

                if (!mainRect || !wrapperRect || !barRect) {
                    setAffixStyle(undefined);
                    return;
                }

                // 当前组件底部如果超出视口，则固定到底部
                const bottomOverflow = wrapperRect.bottom - window.innerHeight;

                if (bottomOverflow > 0) {
                    setAffixStyle((prev) => {
                        const nextStyle: React.CSSProperties = {
                            position: "fixed",
                            bottom: 0,
                            left: mainRect.left,
                            width: wrapperRect.width,
                            zIndex: 800,
                        };

                        if (
                            prev?.position === nextStyle.position &&
                            prev?.bottom === nextStyle.bottom &&
                            prev?.left === nextStyle.left &&
                            prev?.width === nextStyle.width &&
                            prev?.zIndex === nextStyle.zIndex
                        ) {
                            return prev;
                        }

                        return nextStyle;
                    });
                } else {
                    setAffixStyle(undefined);
                }
            }, 200),
        [],
    );

    // 订单列表变化后，重新计算一次
    useEffect(() => {
        const dispose = reaction(
            () => orderManagementStore.table.orders,
            () => {
                setTimeout(() => {
                    updatePosition();
                }, 16);
            },
        );

        return () => {
            dispose();
        };
    }, []);

    // 真实高度变化时，重新计算固定位置
    useEffect(() => {
        updatePosition();
    }, [barSize?.width, barSize?.height]);

    // 滚动和窗口变化时重新计算
    useEffect(() => {
        window.addEventListener("scroll", updatePosition);
        window.addEventListener("resize", updatePosition);

        return () => {
            window.removeEventListener("scroll", updatePosition);
            window.removeEventListener("resize", updatePosition);
            updatePosition.cancel();
        };
    }, []);

    const spacerStyle = useMemo<React.CSSProperties | undefined>(() => {
        if (!affixStyle || !barSize?.height) {
            return undefined;
        }

        return {
            height: barSize.height,
        };
    }, [affixStyle, barSize?.height]);

    return (
        <div ref={wrapperRef}>
            {affixStyle ? <div style={spacerStyle} /> : null}

            <div ref={barRef} style={affixStyle}>
                <RunningStatus shop={shop} instantShipStore={orderManagementStore.instantShipStore} />

                <Pagination shop={shop} tab={tab} />

                <div className={`bottom-operation-bar ${isWrapped ? "is-wrapped" : ""}`}>
                    <div className="bottom-operation-bar-meta">
                        <div
                            ref={metaRef}
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                flexWrap: "wrap",
                                width: "max-content",
                                maxWidth: "100%",
                            }}
                        >
                            <SelectAllCheckbox style={{ userSelect: "none" }}>全选</SelectAllCheckbox>

                            <SelectedOrderInfo selectedOrders={orderManagementStore.table.selectedOrders} />
                        </div>
                    </div>

                    <div className="bottom-operation-bar-actions">
                        <div
                            ref={actionsRef}
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                flexWrap: "wrap",
                                width: "max-content",
                                maxWidth: "100%",
                                justifyContent: "center",
                                gap: "4px 8px ",
                                transform: shiftRight > 0 ? `translateX(${shiftRight}px)` : "none",
                                transition: "transform 0.2s ease-out",
                            }}
                        >
                            <OperationButtons {...props} />
                        </div>
                    </div>

                    <div className="bottom-operation-bar-extra">
                        <BackTopButton />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default observer(BottomOperationBar);
